import { FC, useMemo } from 'react';
import { FaceShadingView } from './types';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const SunAccessHeatmap: FC<{ shading: FaceShadingView }> = ({ shading }) => {
  const { hourlySunAccessPercent, monthlySunAccessPercent } = shading;

  const hours = useMemo(() => {
    const litHours = Array.from({ length: 24 }, (_, hour) => hour).filter(
      (hour) => hourlySunAccessPercent.some((row) => (row[hour] ?? 0) > 0)
    );
    if (litHours.length === 0) return [];
    const first = litHours[0];
    const last = litHours[litHours.length - 1];
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [hourlySunAccessPercent]);

  if (hours.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table
        aria-label="Sun access by month and hour"
        className="border-separate border-spacing-px"
      >
        <thead>
          <tr>
            <th aria-hidden className="w-8" />
            {hours.map((hour) => (
              <th
                key={hour}
                scope="col"
                className="min-w-4 pb-0.5 text-center text-xs font-normal tabular-nums text-gray-400"
              >
                {hour % 2 === 0 ? hour : ''}
              </th>
            ))}
            <th aria-hidden className="w-8" />
          </tr>
        </thead>
        <tbody>
          {hourlySunAccessPercent.map((row, month) => (
            <tr key={month}>
              <th
                scope="row"
                className="pr-1.5 text-right text-xs font-normal text-gray-400"
              >
                {MONTH_LABELS[month]}
              </th>
              {hours.map((hour) => {
                const value = row[hour] ?? 0;
                return (
                  <td
                    key={hour}
                    className="h-4 min-w-4 rounded-sm bg-gray-100"
                    title={`${MONTH_LABELS[month]} ${String(hour).padStart(2, '0')}:00, sun on ${value}% of days`}
                  >
                    <div
                      className="h-full w-full rounded-sm"
                      style={{
                        backgroundColor: `rgba(245, 158, 11, ${(value / 100) * 0.95})`,
                      }}
                    />
                  </td>
                );
              })}
              <td className="pl-1.5 text-right text-xs tabular-nums text-gray-500">
                {monthlySunAccessPercent[month]}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SunAccessHeatmap;
